import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Umbrella, 
  Search, 
  Filter, 
  Calendar, 
  User,
  Users,
  Trash2, 
  Edit, 
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  CalendarDays,
  BarChart3
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ViewTabs } from './ui';
import AdminLeaveModal from './AdminLeaveModal';
import LeaveModal from './LeaveModal';
import LeaveDecisionActions, { canRevokeApprovedLeave, isOwnLeaveRequest } from './LeaveDecisionActions';

const LeaveManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showAdminLeaveModal, setShowAdminLeaveModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveToApprove, setLeaveToApprove] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalData, setApprovalData] = useState({
    status: 'approved',
    admin_comment: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [descriptionLeave, setDescriptionLeave] = useState(null);
  const [leaveView, setLeaveView] = useState('team'); // project_lead: 'team' | 'mine'
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const isProjectLead = user?.role === 'project_lead';
  const canManageTeamLeaves =
    user?.role === 'admin' || (isProjectLead && leaveView === 'team');
  const canApproveLeave = (leave) =>
    canManageTeamLeaves && leave?.status === 'pending' && !isOwnLeaveRequest(user, leave);
  const canRevokeLeave = (leave) => canRevokeApprovedLeave(user, leave);

  const leaveCategories = [
    { value: '', label: 'All Categories' },
    { value: 'annual', label: 'Annual Leave' },
    { value: 'casual', label: 'Casual Leave' },
    { value: 'medical', label: 'Medical Leave' }
  ];

  const categoryOptions = useMemo(
    () =>
      leaveCategories
        .filter((c) => c.value !== '')
        .map((c) => ({ id: c.value, name: c.label })),
    // leaveCategories is a constant inline array — stable option set
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categoryOptions;
    const term = categorySearch.toLowerCase().trim();
    return categoryOptions.filter((c) => c.name.toLowerCase().includes(term));
  }, [categoryOptions, categorySearch]);

  const filteredUsersForDropdown = useMemo(() => {
    if (!userSearch.trim()) return users;
    const term = userSearch.toLowerCase().trim();
    return users.filter(
      (u) =>
        (u.full_name || '').toLowerCase().includes(term) ||
        (u.username || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term)
    );
  }, [users, userSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setOpenFilterDropdown(null);
        setCategorySearch('');
        setUserSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterDropdown]);

  const renderFilterDropdown = (
    type,
    label,
    currentValue,
    displayValue,
    onSelect,
    searchValue,
    onSearchChange,
    filteredItems,
    allLabel
  ) => (
    <div className="w-full relative filter-dropdown">
      <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpenFilterDropdown(openFilterDropdown === type ? null : type)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm">{displayValue || allLabel || `All ${label}`}</span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${
            openFilterDropdown === type ? 'rotate-180' : ''
          }`}
        />
      </button>
      {openFilterDropdown === type && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-72 flex flex-col">
          <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 sticky top-0 bg-white dark:bg-secondary-800 rounded-t-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                className="input-field w-full !pl-9"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            <button
              type="button"
              onClick={() => {
                onSelect('');
                setOpenFilterDropdown(null);
                onSearchChange('');
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                !currentValue
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                  : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
              }`}
            >
              {allLabel || `All ${label}`}
            </button>
            {filteredItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onSelect(item.id);
                  setOpenFilterDropdown(null);
                  onSearchChange('');
                }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  String(currentValue) === String(item.id)
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                }`}
              >
                {item.name || item.full_name || item.username}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div className="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400">
                No matches
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
  const projectLeadTabs = [
    { id: 'team', label: 'Team Leaves', icon: Users },
    { id: 'mine', label: 'My Leaves', icon: User },
  ];

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const showTeamLeaves =
        user?.role === 'admin' || (isProjectLead && leaveView === 'team');
      let url = showTeamLeaves ? '/api/leaves' : '/api/leaves/my-leaves';
      const params = new URLSearchParams();
      
      if (selectedUser && showTeamLeaves) params.append('user_id', selectedUser);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setLeaves(response.data || []);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
      toast.error('Failed to fetch leave data');
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  }, [user?.role, isProjectLead, leaveView, selectedUser, dateRange]);

  const fetchUsers = useCallback(async () => {
    if (!(user?.role === 'admin' || (isProjectLead && leaveView === 'team'))) return;
    
    try {
      const response = await axios.get('/api/team/active');
      const memberUsers = response.data?.filter(u => {
        const isActiveMember =
          (u.role === 'member' || u.role === 'project_lead' || u.role === 'admin') &&
          u.is_disabled !== true &&
          u.is_verified !== false &&
          u.approval_status !== 'declined' &&
          ((u.full_name && u.full_name !== 'Unknown User' && u.full_name.trim() !== '') || u.email);
        if (!isActiveMember) return false;
        // Project leads only filter their assigned reportees
        if (user?.role === 'project_lead') {
          return u.team_lead === user.full_name;
        }
        return true;
      }) || [];
      setUsers(memberUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [user?.role, isProjectLead, leaveView, user?.full_name]);

  useEffect(() => {
    fetchLeaves();
    fetchUsers();
  }, [fetchLeaves, fetchUsers]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedUser('');
  }, [leaveView]);

  const handleApprovalAction = async () => {
    if (!leaveToApprove) return;
    
    setApprovalLoading(true);
    try {
      await axios.put(`/api/leaves/${leaveToApprove.id}/approve`, approvalData);
      const wasRevoke =
        leaveToApprove.status === 'approved' && approvalData.status === 'declined';
      toast.success(
        wasRevoke
          ? 'Leave approval revoked. Days have been returned to the employee’s balance.'
          : `Leave request ${approvalData.status} successfully`
      );
      fetchLeaves();
      setShowApprovalModal(false);
      setLeaveToApprove(null);
      setApprovalData({ status: 'approved', admin_comment: '' });
    } catch (error) {
      console.error('Failed to approve/decline leave:', error);
      toast.error('Failed to process leave request');
    } finally {
      setApprovalLoading(false);
    }
  };

  const openApprovalModal = (leave, action) => {
    setLeaveToApprove(leave);
    setApprovalData({
      status: action,
      admin_comment: ''
    });
    setShowApprovalModal(true);
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'annual': return 'bg-blue-100 text-blue-800';
      case 'casual': return 'bg-green-100 text-green-800';
      case 'medical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'annual': return <Calendar className="h-4 w-4" />;
      case 'casual': return <CheckCircle className="h-4 w-4" />;
      case 'medical': return <AlertTriangle className="h-4 w-4" />;
      default: return <Umbrella className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedUser('');
    setDateRange({ start: '', end: '' });
    setOpenFilterDropdown(null);
    setCategorySearch('');
    setUserSearch('');
  };

  const filteredLeaves = useMemo(() => leaves.filter(leave => {
    if (!leave.user_name || leave.user_name === 'Unknown User' || leave.user_name.trim() === '') {
      return false;
    }
    
    const matchesSearch = !searchTerm || 
      leave.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.user_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !selectedCategory || leave.category === selectedCategory;
    const matchesUser = !selectedUser || leave.user_id === selectedUser;
    
    const matchesDateRange = !dateRange.start || !dateRange.end || 
      (new Date(leave.start_date) >= new Date(dateRange.start) && 
       new Date(leave.end_date) <= new Date(dateRange.end));
    
    return matchesSearch && matchesCategory && matchesUser && matchesDateRange;
  }), [leaves, searchTerm, selectedCategory, selectedUser, dateRange]);

  // Pagination logic
  const totalPages = Math.ceil(filteredLeaves.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLeaves = filteredLeaves.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedUser, dateRange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Umbrella className="h-8 w-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Leave Management</h1>
            <p className="text-secondary-600">
              {user?.role === 'admin'
                ? 'Manage all team leave requests'
                : isProjectLead && leaveView === 'team'
                  ? "Manage your team's leave requests"
                  : 'View and manage your leave requests'}
            </p>
          </div>
        </div>
        {user?.role === 'admin' && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAdminLeaveModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Leave
            </button>
            <button
              onClick={() => navigate('/leave-balance')}
              className="flex items-center gap-2 px-4 py-2 border border-secondary-300 text-secondary-700 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <BarChart3 className="h-4 w-4" />
              Leave Balance
            </button>
          </div>
        )}
      </div>

      {isProjectLead && (
        <ViewTabs
          tabs={projectLeadTabs}
          activeId={leaveView}
          onChange={setLeaveView}
        />
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder={
                  canManageTeamLeaves
                    ? 'Search leave descriptions or user names...'
                    : 'Search leave descriptions...'
                }
                className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {(user?.role === 'member' || user?.role === 'project_lead') && (
              <button
                type="button"
                onClick={() => setShowLeaveModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                data-testid="leave-management-mark-leave"
              >
                <Calendar className="h-4 w-4" />
                Mark Leave
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <Filter className="h-4 w-4" />
              Filters
              {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Clear Filters */}
          {(searchTerm || selectedCategory || selectedUser || dateRange.start || dateRange.end) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 text-secondary-600 hover:text-secondary-800 transition-colors"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {renderFilterDropdown(
              'category',
              'Category',
              selectedCategory,
              leaveCategories.find((c) => c.value && c.value === selectedCategory)?.label || '',
              setSelectedCategory,
              categorySearch,
              setCategorySearch,
              filteredCategories,
              'All Categories'
            )}

            {canManageTeamLeaves &&
              renderFilterDropdown(
                'user',
                'User',
                selectedUser,
                (() => {
                  const u = users.find((x) => String(x.id) === String(selectedUser));
                  return u ? u.full_name || u.username || '' : '';
                })(),
                setSelectedUser,
                userSearch,
                setUserSearch,
                filteredUsersForDropdown,
                'All Users'
              )}

            <div>
              <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                Start Date
              </label>
              <input
                type="date"
                className="input-field"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                End Date
              </label>
              <input
                type="date"
                className="input-field"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              />
            </div>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Umbrella className="h-6 w-6 text-orange-600" />
            <span className="font-medium text-secondary-600">Total Leaves</span>
          </div>
          <div className="text-2xl font-bold text-secondary-900">
            {filteredLeaves.length}
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="h-6 w-6 text-blue-600" />
            <span className="font-medium text-secondary-600">Active Leaves</span>
          </div>
          <div className="text-2xl font-bold text-secondary-900">
            {filteredLeaves.filter(leave => 
              new Date(leave.start_date) <= new Date() && 
              new Date(leave.end_date) >= new Date()
            ).length}
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="h-6 w-6 text-green-600" />
            <span className="font-medium text-secondary-600">Upcoming Leaves</span>
          </div>
          <div className="text-2xl font-bold text-secondary-900">
            {filteredLeaves.filter(leave => 
              new Date(leave.start_date) > new Date()
            ).length}
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="h-6 w-6 text-purple-600" />
            <span className="font-medium text-secondary-600">Completed Leaves</span>
          </div>
          <div className="text-2xl font-bold text-secondary-900">
            {filteredLeaves.filter(leave => 
              new Date(leave.end_date) < new Date()
            ).length}
          </div>
        </div>
      </div>

      {/* Leave List */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredLeaves.length === 0 ? (
          <div className="text-center py-12">
            <Umbrella className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No leave requests found</h3>
            <p className="text-secondary-600">
              {searchTerm || selectedCategory || selectedUser || dateRange.start || dateRange.end
                ? 'Try adjusting your filters to see more results.'
                : isProjectLead && leaveView === 'mine'
                  ? "You haven't submitted any leave requests yet."
                  : isProjectLead && leaveView === 'team'
                    ? 'No leave requests from your team yet.'
                    : 'No leave requests have been submitted yet.'}
            </p>
          </div>
        ) : (
          <>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-secondary-200">
            {currentLeaves.map((leave) => {
              const description = leave.description || '';
              const canExpand = description.length > 90;
              return (
                <div key={leave.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center min-w-0">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-primary-600">
                          {leave.user_name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="ml-3 min-w-0">
                        <div className="text-sm font-medium text-secondary-900 truncate">
                          {leave.user_name || 'Unknown User'}
                        </div>
                        <div className="text-xs text-secondary-500 truncate">{leave.user_email}</div>
                      </div>
                    </div>
                    <span className={`shrink-0 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(leave.status)}`}>
                      {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {getCategoryIcon(leave.category)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(leave.category)}`}>
                      {leave.category}
                    </span>
                    <span className="text-xs text-secondary-500">
                      {format(parseISO(leave.start_date), 'MMM d, yyyy')}
                      {format(parseISO(leave.start_date), 'MMM d, yyyy') !== format(parseISO(leave.end_date), 'MMM d, yyyy') &&
                        ` – ${format(parseISO(leave.end_date), 'MMM d, yyyy')}`}
                    </span>
                    <span className="text-xs font-medium text-secondary-700">
                      {leave.working_days ?? '—'} working day{(leave.working_days === 1) ? '' : 's'}
                    </span>
                  </div>
                  <p className="text-sm text-secondary-700 break-words line-clamp-3">{description || '—'}</p>
                  {canManageTeamLeaves && (
                    <div className="text-xs text-secondary-500 space-y-0.5">
                      <div>Intended to: {leave.team_lead_name || 'Not Assigned'}</div>
                      <div>
                        Approved by:{' '}
                        {leave.status === 'pending' ? '—' : leave.approved_by_name || '—'}
                      </div>
                    </div>
                  )}
                  {canExpand && (
                    <button
                      type="button"
                      onClick={() => setDescriptionLeave(leave)}
                      className="text-xs font-medium text-primary-600 min-h-[44px]"
                    >
                      Show more
                    </button>
                  )}
                  {canManageTeamLeaves && (
                    <LeaveDecisionActions
                      leave={leave}
                      canApprove={canApproveLeave(leave)}
                      canRevoke={canRevokeLeave(leave)}
                      onApprove={(row) => openApprovalModal(row, 'approved')}
                      onDecline={(row) => openApprovalModal(row, 'declined')}
                      onRevoke={(row) => openApprovalModal(row, 'declined')}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Date Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Working Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th>
                  {canManageTeamLeaves && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Intended to
                    </th>
                  )}
                  {canManageTeamLeaves && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Approved By
                    </th>
                  )}
                  {canManageTeamLeaves && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {currentLeaves.map(leave => {
                  const description = leave.description || '';
                  const canExpand = description.length > 90;

                  return (
                    <tr key={leave.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-600">
                              {leave.user_name?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-medium text-secondary-900 truncate max-w-[140px]">
                              {leave.user_name || 'Unknown User'}
                            </div>
                            <div className="text-sm text-secondary-500 truncate max-w-[140px]">
                              {leave.user_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(leave.category)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(leave.category)}`}>
                            {leave.category}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <p className="text-sm text-secondary-900 break-words line-clamp-2">
                          {description || '—'}
                        </p>
                        {canExpand && (
                          <button
                            type="button"
                            onClick={() => setDescriptionLeave(leave)}
                            className="mt-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                          >
                            Show more
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-secondary-900">
                          <div>{format(parseISO(leave.start_date), 'MMM d, yyyy')}</div>
                          {format(parseISO(leave.start_date), 'MMM d, yyyy') !== format(parseISO(leave.end_date), 'MMM d, yyyy') && (
                            <>
                              <div className="text-secondary-500">to</div>
                              <div>{format(parseISO(leave.end_date), 'MMM d, yyyy')}</div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-secondary-900">
                          {leave.working_days != null ? leave.working_days : '—'}
                        </div>
                        <div className="text-xs text-secondary-500">
                          working day{(leave.working_days === 1) ? '' : 's'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(leave.status)}`}>
                          {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                        </span>
                        {leave.admin_comment && (
                          <div className="mt-1 text-xs text-secondary-500 max-w-[140px] line-clamp-2" title={leave.admin_comment}>
                            {leave.admin_comment}
                          </div>
                        )}
                      </td>
                      {canManageTeamLeaves && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900 truncate max-w-[120px]" title={leave.team_lead_name || 'Not Assigned'}>
                            {leave.team_lead_name || 'Not Assigned'}
                          </div>
                        </td>
                      )}
                      {canManageTeamLeaves && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className="text-sm text-secondary-900 truncate max-w-[120px]"
                            title={leave.approved_by_name || undefined}
                          >
                            {leave.status === 'pending'
                              ? '—'
                              : leave.approved_by_name || '—'}
                          </div>
                        </td>
                      )}
                      {canManageTeamLeaves && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <LeaveDecisionActions
                            leave={leave}
                            canApprove={canApproveLeave(leave)}
                            canRevoke={canRevokeLeave(leave)}
                            onApprove={(row) => openApprovalModal(row, 'approved')}
                            onDecline={(row) => openApprovalModal(row, 'declined')}
                            onRevoke={(row) => openApprovalModal(row, 'declined')}
                            compact
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Pagination */}
        {filteredLeaves.length > itemsPerPage && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-secondary-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-secondary-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredLeaves.length)}</span> of{' '}
                  <span className="font-medium">{filteredLeaves.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronUp className="h-5 w-5" style={{ transform: 'rotate(-90deg)' }} />
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-secondary-300 text-secondary-500 hover:bg-secondary-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronUp className="h-5 w-5" style={{ transform: 'rotate(90deg)' }} />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Description Modal */}
      {descriptionLeave && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">Leave Description</h3>
                <p className="text-sm text-secondary-500 mt-1">
                  {descriptionLeave.user_name || 'Unknown User'}
                  {descriptionLeave.category ? ` · ${descriptionLeave.category}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDescriptionLeave(null)}
                className="p-1 rounded-md text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100 transition-colors"
                aria-label="Close description"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <p className="text-sm text-secondary-900 whitespace-pre-wrap break-words leading-relaxed">
                {descriptionLeave.description || '—'}
              </p>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setDescriptionLeave(null)}
                className="px-4 py-2 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && leaveToApprove && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              {leaveToApprove.status === 'approved' && approvalData.status === 'declined'
                ? 'Revoke Approved Leave'
                : approvalData.status === 'approved'
                  ? 'Approve Leave Request'
                  : 'Decline Leave Request'}
            </h3>
            
            <div className="mb-4 space-y-2">
              <p className="text-sm text-secondary-600">
                Employee: <span className="font-medium text-secondary-900">{leaveToApprove.user_name}</span>
              </p>
              <p className="text-sm text-secondary-600">
                Leave Type: <span className="font-medium text-secondary-900">{leaveToApprove.category}</span>
              </p>
              <div className="text-sm text-secondary-600">
                <span className="block mb-1">Description:</span>
                <p className="font-medium text-secondary-900 break-words max-h-32 overflow-y-auto rounded-md bg-secondary-50 p-2">
                  {leaveToApprove.description || '—'}
                </p>
              </div>
              {leaveToApprove.status === 'approved' && approvalData.status === 'declined' && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                  This marks the leave as declined and returns the working days to the employee’s balance.
                </p>
              )}
            </div>

            {approvalData.status === 'declined' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  {leaveToApprove.status === 'approved' ? 'Reason for revoke *' : 'Reason for Decline *'}
                </label>
                <textarea
                  value={approvalData.admin_comment}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, admin_comment: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows="3"
                  placeholder={
                    leaveToApprove.status === 'approved'
                      ? 'Explain why this approval is being revoked...'
                      : 'Please provide a reason for declining this leave request...'
                  }
                  required
                />
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setLeaveToApprove(null);
                  setApprovalData({ status: 'approved', admin_comment: '' });
                }}
                className="px-4 py-2 text-secondary-600 hover:text-secondary-800 transition-colors"
                disabled={approvalLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleApprovalAction}
                disabled={approvalLoading || (approvalData.status === 'declined' && !approvalData.admin_comment.trim())}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  approvalData.status === 'approved'
                    ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-green-300'
                    : 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300'
                }`}
              >
                {approvalLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </div>
                ) : leaveToApprove.status === 'approved' && approvalData.status === 'declined' ? (
                  'Revoke approval'
                ) : approvalData.status === 'approved' ? (
                  'Approve'
                ) : (
                  'Decline'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {user?.role === 'admin' && (
        <AdminLeaveModal
          isOpen={showAdminLeaveModal}
          onClose={() => setShowAdminLeaveModal(false)}
          onSuccess={fetchLeaves}
        />
      )}

      {(user?.role === 'member' || user?.role === 'project_lead') && (
        <LeaveModal
          isOpen={showLeaveModal}
          onClose={() => setShowLeaveModal(false)}
          onSuccess={fetchLeaves}
        />
      )}
    </div>
  );
};

export default LeaveManagement;
